'use client';

import { type ReactNode } from 'react';
import { BackButton } from './back-button';
import { HeaderActions } from './header-actions';
import { useMobileNavigation } from './use-mobile-navigation';
import type { MobileHeaderVariant } from '@components/layout/page-chrome-context';

interface NavigationBarProps {
  organization_id: string;
  trailing?: ReactNode;
  variant?: MobileHeaderVariant;
}

/** The toolbar keeps its geometry while the large title scrolls beneath it. */
export function NavigationBar({
  organization_id,
  trailing,
  variant
}: NavigationBarProps) {
  const navigation = useMobileNavigation(organization_id);
  const { title, isRoot, parentHref, parentLabel, chrome } = navigation;
  const resolvedVariant = variant ?? navigation.variant;
  const trailingContent = chrome?.trailingAction ?? trailing;
  const balanceEmptyTrailing =
    Boolean(chrome?.hideDefaultActions) && !trailingContent;
  const showBack = !isRoot || Boolean(chrome?.backHref);

  return (
    <div
      data-mobile-toolbar=""
      data-large-title={resolvedVariant === 'large-title'}
      className="mobile-toolbar shrink-0 bg-background pt-[calc(var(--safe-top)+12px)] desktop-chrome:hidden"
    >
      <div className="grid min-h-[var(--mobile-navbar-height)] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-5">
        <div className="flex min-w-0 items-center">
          {showBack ? (
            <BackButton href={parentHref} label={parentLabel} />
          ) : null}
        </div>
        <div className="mobile-toolbar-title min-w-0 truncate text-center text-card-title">
          <span>{title}</span>
          {chrome?.status ? (
            <span className="ml-2">{chrome.status}</span>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          {trailingContent}
          {!chrome?.hideDefaultActions && (
            <HeaderActions organization_id={organization_id} />
          )}
          {balanceEmptyTrailing ? (
            <span className="size-11 shrink-0" aria-hidden />
          ) : null}
        </div>
      </div>
      {resolvedVariant !== 'transparent' ? (
        <div className="mobile-toolbar-divider" aria-hidden />
      ) : null}
    </div>
  );
}

/** In normal scroll flow: never remove this row in response to scrolling. */
export function MobileLargeTitle({
  organization_id
}: {
  organization_id: string;
}) {
  const { title, variant } = useMobileNavigation(organization_id);
  if (variant !== 'large-title') return null;

  return (
    <div
      data-mobile-large-title=""
      aria-hidden
      className="shrink-0 px-5 pt-5 pb-2 desktop-chrome:hidden"
    >
      <div
        data-mobile-title-text=""
        className="mobile-large-title text-app-large-title text-foreground break-words"
      >
        {title}
      </div>
    </div>
  );
}
